import { getStudioOptionLabel } from '@/pages/customer/studio/utils/studioOptionLabel';

const OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;

const isObjectIdString = (value) => OBJECT_ID_PATTERN.test(String(value).trim());

const resolveLanguageShort = (language) => {
	if (!language) {
		return '';
	}

	if (typeof language === 'string') {
		return language;
	}

	return language.short || language.code || '';
};

const getPersonalityKinks = (profile) => {
	const entry = profile?.personality?.find((item) => {
		const label = item?.label?.toLowerCase();

		return label === 'kinks' || label === 'preferences';
	});

	return entry?.value ?? null;
};

const splitKinkString = (value) => {
	const trimmed = value.trim();

	if (!trimmed) {
		return [];
	}

	if (/[·,;|/]/.test(trimmed)) {
		return trimmed
			.split(/[·,;|/]/)
			.map((part) => part.trim())
			.filter(Boolean);
	}

	return [trimmed];
};

export const flattenKinkEntries = (kinks) => {
	if (kinks === null || kinks === undefined) {
		return [];
	}

	if (Array.isArray(kinks)) {
		if (!kinks.length) {
			return [];
		}

		return kinks.flatMap((entry) => flattenKinkEntries(entry));
	}

	if (typeof kinks === 'string') {
		return splitKinkString(kinks);
	}

	if (typeof kinks === 'object') {
		return [kinks];
	}

	return [];
};

const resolveKinkEntryLabel = (entry, lang) => {
	if (!entry) {
		return '';
	}

	if (typeof entry === 'string') {
		const trimmed = entry.trim();

		if (!trimmed || isObjectIdString(trimmed)) {
			return '';
		}

		return getStudioOptionLabel(lang, 'kinks', trimmed) || trimmed;
	}

	if (typeof entry !== 'object') {
		return '';
	}

	if (typeof entry.label === 'string' && entry.label.trim()) {
		return entry.label.trim();
	}

	const translations = Array.isArray(entry.translations) ? entry.translations : [];
	const localized = translations.find(
		(translation) => resolveLanguageShort(translation.language) === lang,
	)?.label;

	if (localized?.trim()) {
		return localized.trim();
	}

	const fallbackTranslation = translations.find(
		(translation) => translation.label?.trim(),
	)?.label;

	if (fallbackTranslation?.trim()) {
		return fallbackTranslation.trim();
	}

	if (typeof entry.value === 'string' && entry.value.trim()) {
		return getStudioOptionLabel(lang, 'kinks', entry.value.trim()) || entry.value.trim();
	}

	return '';
};

const collectProfileKinkEntries = (profile) => [
	...flattenKinkEntries(profile?.kinks),
	...flattenKinkEntries(profile?.person_config?.kinks),
	...flattenKinkEntries(getPersonalityKinks(profile)),
];

const resolveKinkLabels = (entries, lang) => {
	const seen = new Set();
	const resolved = [];

	entries.forEach((entry) => {
		const label = resolveKinkEntryLabel(entry, lang);

		if (!label) {
			return;
		}

		const dedupeKey = label.toLowerCase();

		if (seen.has(dedupeKey)) {
			return;
		}

		seen.add(dedupeKey);
		resolved.push(label);
	});

	return resolved;
};

export const resolveProfileKinks = (profile, lang, fallback) => {
	const fromProfile = resolveKinkLabels(collectProfileKinkEntries(profile), lang);

	if (fromProfile.length) {
		return fromProfile;
	}

	return resolveKinkLabels(flattenKinkEntries(fallback?.kinks), lang);
};

export const formatProfileKinks = (profile, lang, t, fallback) => {
	const resolvedKinks = resolveProfileKinks(profile, lang, fallback);

	if (resolvedKinks.length) {
		return resolvedKinks.join(' · ');
	}

	return t.defaultKinks;
};
